// This is Auth Slice
import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {RootState} from '../../store'; //  RootState type
import {User} from '../../utils/types';
import {AuthState} from './types';
import {getLS, removeLS, setLS} from "../../helpers/ls";
import AuthService from "./auth_services";
import setAuthToken from "../../utils/setAuthToken";

// Register User Async Thunk
export const registerUser = createAsyncThunk(
    'auth/registerUser',
    async (user: Partial<User>, {dispatch, rejectWithValue}) => {
        try {
            // Make API request
            const data = await AuthService.register(user)

            // If response is not ok, throw error
            if (!data) {
                return rejectWithValue('Something went wrong with your request');
            }

            // Set token in local storage
            setLS('refresh_token', data.data.refreshToken)
            setLS('access_token', data.data.accessToken)
            setAuthToken(data.data.accessToken)

            // If response is ok, return user
            return data.data;
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

// Register User with Google Async Thunk
export const registerWithGoogle = createAsyncThunk(
    'auth/registerWithGoogle',
    async ({token, referral_code, signup_attribution}:{token: string, referral_code?: string, signup_attribution?: Record<string, string>}, {rejectWithValue}) => {
        try {
            // Make API request
            const data = await AuthService.registerWithGoogle(token, referral_code, signup_attribution)

            // If response is not ok, throw error
            if (!data) {
                return rejectWithValue('Something went wrong with your request');
            }

            // Set token in local storage
            setLS('refresh_token', data.data.refreshToken)
            setLS('access_token', data.data.accessToken)
            setAuthToken(data.data.accessToken)

            // If response is ok, return user
            return data.data;
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data.message || error.message);
        }
    }
);

export const registerWithApple = createAsyncThunk(
    'auth/registerWithApple',
    async (payload: any, {rejectWithValue}) => {
        try {
            const data = await AuthService.registerWithApple(payload);
            if (!data) return rejectWithValue('Something went wrong with your request');

            setLS('refresh_token', data.data.refreshToken);
            setLS('access_token', data.data.accessToken);
            setAuthToken(data.data.accessToken);
            return data.data;
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);
// Login User Async Thunk
export const loginStart = createAsyncThunk(
    'auth/loginStart',
    async (user: Partial<User>, {dispatch, rejectWithValue}) => {
        try {
            // Make API request
            const data = await AuthService.login(user)

            // If response is not ok, throw error
            if (!data) {
                return rejectWithValue('Something went wrong with your request');
            }

            // Set email in redux store
            dispatch(setEmail(user.email))
            // Set password in redux store
            dispatch(setPassword(user.password))

            if (data?.data?.accessToken && data?.data?.refreshToken) {
                setLS('refresh_token', data.data.refreshToken)
                setLS('access_token', data.data.accessToken)
                setAuthToken(data.data.accessToken)
            }

            // If response is ok, return otp and expires in
            return data;
        } catch (error: any) {
            const errorResponse = error?.response?.data?.message || error.message;
            if (errorResponse) {
                return rejectWithValue(errorResponse);
            } else {
                return rejectWithValue('Something went wrong with your request');
            }

        }
    }
);

// Confirm User Login
export const loginConfirm = createAsyncThunk(
    'auth/loginConfirm',
    async (otp: string, {dispatch, getState, rejectWithValue}) => {
        try {
            const state = getState() as RootState;
            const email: string = state.auth.email as string;

            // Make API request
            const data = await AuthService.confirm(email, otp);


            // Set token in local storage
            setLS('refresh_token', data.data.refreshToken)
            setLS('access_token', data.data.accessToken)
            setAuthToken(data.data.accessToken)
            // dispatch(fetchUser())
            return data.data;
        } catch (error: any) {
            const errorResponse = error?.response?.data?.message || error.message;
            if (errorResponse) {
                return rejectWithValue(errorResponse);
            } else {
                return rejectWithValue('Something went wrong with your request');
            }
        }
    }
)

// Login User With Google Async thunk
export const loginWithGoogle = createAsyncThunk(
    'auth/loginWithGoogle',
    async ({token, referral_code, signup_attribution}: {token: string, referral_code?: string, signup_attribution?: Record<string, string>}, {rejectWithValue}) => {
        try {
            // Make API request
            const data = await AuthService.loginWithGoogle(token, referral_code, signup_attribution)

            // If response is not ok, throw error
            if (!data) {
                return rejectWithValue('Something went wrong with your request');
            }

            // Set token in local storage
            setLS('refresh_token', data.data.refreshToken)
            setLS('access_token', data.data.accessToken)
            setAuthToken(data.data.accessToken)

            // If response is ok, return user
            return data.data;
        } catch (error: any) {
            let errorResponse = error?.response?.data?.message || error.message;
            // If server error, throw error
            return rejectWithValue(errorResponse);
        }
    }
);

// Login User With Google Async thunk
export const loginWithApple = createAsyncThunk(
    'auth/loginWithApple',
    async (payload: any, {rejectWithValue}) => {
        try {
            // Make API request
            const data = await AuthService.loginWithApple(payload)

            // If response is not ok, throw error
            if (!data) {
                return rejectWithValue('Something went wrong with your request');
            }

            // Set token in local storage
            setLS('refresh_token', data.data.refreshToken)
            setLS('access_token', data.data.accessToken)
            setAuthToken(data.data.accessToken)

            // If response is ok, return user
            return data.data;
        } catch (error: any) {
            let errorResponse = error?.response?.data?.message || error.message;
            // If server error, throw error
            return rejectWithValue(errorResponse);
        }
    }
);

// Fetch User Async Thunk
export const fetchUser = createAsyncThunk(
    'auth/fetchUser',
    async (_, {rejectWithValue}) => {
        try {
            // Make API request
            const data = await AuthService.fetchUser()

            // If response is not ok, throw error
            if (!data) {
                return rejectWithValue('Something went wrong with your request');
            }

            // If response is ok, return user
            return data.data;
        } catch (error: any) {
            removeLS('refresh_token');
            removeLS('access_token');
            // If server error, throw error
            return rejectWithValue(error.response.data.message || error.message);
        }
    }
);

// Forgot Password Async Thunk
export const forgotPassword = createAsyncThunk(
    'auth/forgotPassword',
    async (email: string, {rejectWithValue}) => {
        try {
            // Make API request
            const data = await AuthService.forgotPassword(email)

            // If response is not ok, throw error
            if (!data) {
                return rejectWithValue('Something went wrong with your request');
            }

            // If response is ok, return user
            return data.data;
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error.response.data.message || error.message);
        }
    }
);

// Reset Password Async Thunk
export const resetPassword = createAsyncThunk(
    'auth/resetPassword',
    async (payload: { email: string, otp: string, password: string }, {rejectWithValue}) => {
        try {
            // Make API request
            const data = await AuthService.resetPassword(payload.email, payload.otp, payload.password)

            // If response is not ok, throw error
            if (!data) {
                return rejectWithValue('Something went wrong with your request');
            }

            // If response is ok, return user
            return data.data;
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error.response.data.message || error.message);
        }
    }
);

// Logout User
export const logoutUser = createAsyncThunk(
    'auth/logoutUser',
    async (_, {getState, rejectWithValue}) => {
        try {
            const state = await getState() as RootState;
            const token = state.auth.refresh_token as string;

            // Make API request
            const data = await AuthService.logout(token || getLS('refresh_token'))

            // If response is not ok, throw error
            if (!data) {
                return rejectWithValue('Something went wrong with your request');
            }

            // If response is ok, return user
            return data.data;
        } catch (error: any) {
            removeLS('refresh_token');
            removeLS('access_token');
            // If server error, throw error
            return rejectWithValue(error.response.data.message || error.message);
        }
    }
)

// Update User Async Thunk
export const updateUser = createAsyncThunk(
    'auth/updateUser',
    async (user: Partial<User>, {rejectWithValue}) => {
        try {
            // Make API request
            const data = await AuthService.updateUser(user)

            // If response is not ok, throw error
            if (!data) {
                return rejectWithValue('Something went wrong with your request');
            }

            // If response is ok, return user
            return data.data;
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error.response.data.message || error.message);
        }
    }
);

// Initial state of the auth feature
const initialState: AuthState = {
    email: null,
    user: null,
    password: null,
    otp: null,
    expires_in: null,
    access_token: getLS('access_token') || null,
    refresh_token: getLS('refresh_token') || null,
    authenticated: false,
    loading: false,
    error: null,
};

// Create auth slice
const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        // Login Reducers
        setEmail: (state, action) => {
            state.email = action.payload
        },
        // Set password
        setPassword: (state, action) => {
            state.password = action.payload
        },
        // OTP Reducers
        setOTP: (state, action) => {
            state.otp = action.payload
        },
        setDefaultPart: (state, action) => {
            if (state.user) {
                state.user.default_part = action.payload;
            }
        }

    },
    extraReducers: (builder) => {
        builder.addCase(registerUser.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(registerUser.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.authenticated = true;
                state.access_token = action.payload.accessToken;
                state.refresh_token = action.payload.refreshToken;
            })
            .addCase(registerUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                state.authenticated = false;
            });
        builder.addCase(registerWithGoogle.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(registerWithGoogle.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.authenticated = true;
                state.access_token = action.payload.accessToken;
                state.refresh_token = action.payload.refreshToken;
            })
            .addCase(registerWithGoogle.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                state.authenticated = false;
            });
        builder.addCase(registerWithApple.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(registerWithApple.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.authenticated = true;
                state.access_token = action.payload.accessToken;
                state.refresh_token = action.payload.refreshToken;
            })
            .addCase(registerWithApple.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                state.authenticated = false;
            });
        builder
            .addCase(loginStart.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(loginStart.fulfilled, (state, action) => {
                state.otp = action.payload.data?.otp || null;
                state.expires_in = action.payload.data?.expires_in || null;
                state.access_token = action.payload.data?.accessToken || null;
                state.refresh_token = action.payload.data?.refreshToken || null;
                state.loading = false;
                state.error = null;
                state.authenticated = Boolean(action.payload.data?.accessToken);
            })
            .addCase(loginStart.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                state.authenticated = false;
            });
        builder.addCase(loginConfirm.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(loginConfirm.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.authenticated = true;
                state.access_token = action.payload.accessToken;
                state.refresh_token = action.payload.refreshToken;
            })
            .addCase(loginConfirm.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                state.authenticated = false;
            });
        builder.addCase(loginWithGoogle.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(loginWithGoogle.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.authenticated = true;
                state.access_token = action.payload.accessToken;
                state.refresh_token = action.payload.refreshToken;
            })
            .addCase(loginWithGoogle.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                state.authenticated = false;
            });
        builder.addCase(loginWithApple.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(loginWithApple.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.authenticated = true;
                state.access_token = action.payload.accessToken;
                state.refresh_token = action.payload.refreshToken;
            })
            .addCase(loginWithApple.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                state.authenticated = false;
            });
        builder.addCase(fetchUser.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchUser.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.authenticated = true;
                state.user = action.payload;
            })
            .addCase(fetchUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                state.authenticated = false;
                removeLS('refresh_token');
                removeLS('access_token');
            });
        builder.addCase(forgotPassword.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(forgotPassword.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.authenticated = false;
            })
            .addCase(forgotPassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                state.authenticated = false;
            });
        builder.addCase(resetPassword.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(resetPassword.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.authenticated = false;
            })
            .addCase(resetPassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                state.authenticated = false;
            });
        builder.addCase(logoutUser.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(logoutUser.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.authenticated = false;
                state.user = null;
                removeLS('refresh_token');
                removeLS('access_token');
            })
            .addCase(logoutUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                state.authenticated = false;
                removeLS('refresh_token');
                removeLS('access_token');
            });
        builder.addCase(updateUser.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(updateUser.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.user = action.payload;
            })
            .addCase(updateUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

    }

});

// Export actions
export const {setEmail, setPassword, setOTP, setDefaultPart} = authSlice.actions;

// Export reducer
export default authSlice.reducer;

// Selector
export const selectAuthState = (state: RootState) => state.auth;
